import config from 'config';
import { generateUUID } from 'utils/uuid';
import { createCache } from './cache';

type FetchApiOptions = {
  token?: string;
};

const host = config.apiURL;

const cache = createCache();

const DEVICE_ID_COOKIE = 'hiboo__deviceId';
const DEVICE_ID_COOKIE_HOST = 'hiboo.io';
const DEVICE_ID_COOKIE_DOMAIN = `.${DEVICE_ID_COOKIE_HOST}`;
const DEVICE_ID_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 5;

const readCookie = (name: string): string | null => {
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
};

// A browser drops a cookie whose Domain it is not under, which would redraw the
// device id on every call — losing the guest inspections only that id reads back.
const deviceIdCookieDomainAttribute = (): string => {
  const { hostname } = window.location;
  const onHibooDomain =
    hostname === DEVICE_ID_COOKIE_HOST ||
    hostname.endsWith(`.${DEVICE_ID_COOKIE_HOST}`);
  return onHibooDomain ? `; Domain=${DEVICE_ID_COOKIE_DOMAIN}` : '';
};

const writeDeviceIdCookie = (value: string) => {
  document.cookie = `${DEVICE_ID_COOKIE}=${encodeURIComponent(value)}${deviceIdCookieDomainAttribute()}; Path=/; Max-Age=${DEVICE_ID_COOKIE_MAX_AGE}; SameSite=Lax`;
};

const getDeviceId = (): string => {
  const existing = readCookie(DEVICE_ID_COOKIE);
  if (existing) return existing;
  const deviceId = generateUUID();
  writeDeviceIdCookie(deviceId);
  return deviceId;
};

const generateEndpoint = (path: string) => `${host}${path}`;

const fetchOptions = (options: FetchApiOptions) => {
  const headers: { [key: string]: string } = {
    accept: 'application/json',
    'Content-Type': 'application/json',
    'x-device-id': getDeviceId(),
  };
  if (options.token) {
    headers['x-access-token'] = options.token;
  }

  return {
    headers: new Headers(headers),
  };
};

const setToken = (token: string) => {
  return createFetchApi({ token: token });
};

const validateResponse = (response: Response) => {
  if (response.status >= 200 && response.status < 400) {
    return response.json().catch(() => null);
  }

  if (response.status === 401) {
    return response;
  }
  return response.json().then((r) => {
    // yes, this is totally crazy. Initially, the response content was thrown and eslint was happy because `r` is any.
    // That means the response was just lost except when the API replies with a 401, go figure… Anyway, since a lot of
    // code relies on that behavior, it is preserved for now and the response is added back to its body until we
    // refactor that in a sane way see https://linear.app/hiboo/issue/FLT-1633

    throw {
      ...r,
      originalResponse: response,
    };
  });
};

export type FetchApi = ReturnType<typeof createFetchApi>;

export const createFetchApi = (options: FetchApiOptions = {}) => ({
  generateEndpoint,
  get: (path: string, getOptions = { cache: true }) => {
    const cacheKey = path;

    const url = `${host}${path}`;

    if (getOptions.cache && cache.hasValidCache(cacheKey, 60)) {
      return cache.getCache(cacheKey);
    }

    const promise = fetch(url, {
      ...fetchOptions(options),
    }).then(validateResponse);

    cache.setCache(cacheKey, promise);
    return promise;
  },

  postFile: (path: string, payload: { [key: string]: string }, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    for (const key of Object.keys(payload)) {
      formData.append(key, payload[key]);
    }

    const headers = new Headers();
    headers.append('x-device-id', getDeviceId());
    if (options.token) {
      headers.append('x-access-token', options.token);
    }
    return fetch(generateEndpoint(path), {
      method: 'POST',
      body: formData,
      headers,
    }).then(validateResponse);
  },

  postFiles: (
    path: string,
    payload: Record<string, string | Blob | number | undefined>,
    files: File[],
  ) => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('file', file);
    }
    for (const key of Object.keys(payload)) {
      const value = payload[key];
      if (value !== undefined) {
        formData.append(key, value instanceof Blob ? value : String(value));
      }
    }

    const headers = new Headers();
    headers.append('x-device-id', getDeviceId());
    if (options.token) {
      headers.append('x-access-token', options.token);
    }
    return fetch(generateEndpoint(path), {
      method: 'POST',
      body: formData,
      headers,
    }).then(validateResponse);
  },
  post: (
    path: string,
    payload: Record<string, unknown>,
    skipValidateResponse = false,
  ) => {
    return fetch(generateEndpoint(path), {
      method: 'POST',
      body: payload ? JSON.stringify(payload) : undefined,
      ...fetchOptions(options),
    }).then((response) => {
      if (skipValidateResponse) {
        return response;
      }
      return validateResponse(response);
    });
  },
  patch: (
    path: string,
    payload: Record<string, unknown>,
    skipValidateResponse = false,
  ) => {
    return fetch(generateEndpoint(path), {
      method: 'PATCH',
      body: payload ? JSON.stringify(payload) : undefined,
      ...fetchOptions(options),
    }).then((response) => {
      if (skipValidateResponse) {
        return response;
      }
      return validateResponse(response);
    });
  },
  setToken,
});
